export class SessionTokenBackoffError extends Error {
	constructor(message, retryAfterMs) {
		super(message);
		this.name = 'SessionTokenBackoffError';
		this.retryAfterMs = retryAfterMs;
	}
}

export class SessionTokenManager {
	/**
	 * @param {object} config Config instance (must support get()).
	 * @param {object} options
	 * @param {import('../core/sensor-integrity.js').SensorIntegrity|null} options.sensorIntegrity
	 * @param {object|null} options.logger Logger with debug/warn (optional).
	 */
	constructor(config, options = {}) {
		this.config = config;
		this.sensorIntegrity = options.sensorIntegrity || null;
		this.logger = options.logger || null;

		this._token = null;
		this._expiresAtMs = 0;
		this._inFlight = null;
		this._blockedUntilMs = 0;
	}

	_isTokenFreshEnough() {
		if (!this._token) return false;
		if (!this._expiresAtMs) return false;
		const windowSeconds = Number(this.config?.get?.('sessionTokenRefreshWindowSeconds')) || 300;
		const refreshWindowMs = Math.max(0, windowSeconds) * 1000;
		return Date.now() < (this._expiresAtMs - refreshWindowMs);
	}

	_isExpiredOrNearExpiry() {
		if (!this._token || !this._expiresAtMs) return true;
		const windowSeconds = Number(this.config?.get?.('sessionTokenRefreshWindowSeconds')) || 300;
		const refreshWindowMs = Math.max(0, windowSeconds) * 1000;
		return Date.now() >= (this._expiresAtMs - refreshWindowMs);
	}

	_getBackoffMs() {
		const seconds = Number(this.config?.get?.('sessionTokenFailureBackoffSeconds')) || 10;
		return Math.max(0, seconds) * 1000;
	}

	async _getSensorHash() {
		try {
			if (this.sensorIntegrity && typeof this.sensorIntegrity.getSensorHash === 'function') {
				return await this.sensorIntegrity.getSensorHash();
			}
		} catch {
		}
		return (this.config?.get?.('sensorHash')) || null;
	}

	async _requestNewToken() {
		const now = Date.now();
		if (now < this._blockedUntilMs) {
			throw new SessionTokenBackoffError('Session token refresh is temporarily paused', this._blockedUntilMs - now);
		}

		const tokenEndpoint = this.config?.get?.('tokenEndpoint');
		const profileId = (this.config?.get?.('profileId') || '').trim();
		if (!tokenEndpoint || typeof tokenEndpoint !== 'string') {
			throw new Error('tokenEndpoint is not configured');
		}
		if (!profileId) {
			throw new Error('profileId is not configured');
		}

		const headers = {
			'Content-Type': 'application/json',
			'Accept': 'application/json'
		};

		const sensorHash = await this._getSensorHash();
		if (sensorHash) {
			headers['X-Sensor-Integrity'] = sensorHash;
		}

		const timeout = Number(this.config?.get?.('timeout')) || 15000;
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), timeout);

		let res;
		try {
			res = await fetch(tokenEndpoint, {
				method: 'POST',
				headers,
				body: JSON.stringify({ profile_id: profileId }),
				signal: controller.signal
			});
		} catch (error) {
			clearTimeout(timeoutId);
			const backoffMs = this._getBackoffMs();
			this._blockedUntilMs = Date.now() + backoffMs;
			if (error.name === 'AbortError') {
				throw new Error('Session token request timeout');
			}
			throw error;
		}
		clearTimeout(timeoutId);

		if (!res.ok) {
			const backoffMs = this._getBackoffMs();
			this._blockedUntilMs = Date.now() + backoffMs;
			throw new Error(`Session token request failed (${res.status})`);
		}

		const json = await res.json().catch(() => null);
		const sessionToken = json && typeof json.session_token === 'string' ? json.session_token : '';
		const expiresIn = json && typeof json.expires_in === 'number' ? json.expires_in : Number(json && json.expires_in);

		if (!sessionToken) {
			const backoffMs = this._getBackoffMs();
			this._blockedUntilMs = Date.now() + backoffMs;
			throw new Error('Session token response missing session_token');
		}

		const ttlSeconds = Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 3600;
		this._token = sessionToken;
		this._expiresAtMs = Date.now() + (ttlSeconds * 1000);

		return sessionToken;
	}

	/**
	 * Returns a valid session JWT (cached in memory).
	 * Refreshes preemptively using sessionTokenRefreshWindowSeconds.
	 */
	async getToken() {
		if (this._isTokenFreshEnough()) return this._token;
		if (this._inFlight) return this._inFlight;

		this._inFlight = (async () => {
			try {
				if (this._isTokenFreshEnough()) return this._token;
				if (this._isExpiredOrNearExpiry()) {
					return await this._requestNewToken();
				}
				return this._token;
			} finally {
				this._inFlight = null;
			}
		})();

		return this._inFlight;
	}

	/**
	 * Forces a refresh. Used after 401/403 once.
	 */
	async forceRefresh() {
		if (this._inFlight) {
			return this._inFlight;
		}
		this._inFlight = (async () => {
			try {
				return await this._requestNewToken();
			} finally {
				this._inFlight = null;
			}
		})();
		return this._inFlight;
	}
}

export default SessionTokenManager;

